/**
 * Web Crypto helpers (Workers, Node 22+ and browsers): tokens, hashing and
 * webhook signatures.
 */
const enc = new TextEncoder()

export function toHex(buf: ArrayBuffer | Uint8Array) {
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

export function randomToken(prefix: string, bytes = 24) {
  const b = crypto.getRandomValues(new Uint8Array(bytes))
  const s = btoa(String.fromCharCode(...b)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  return `${prefix}${s}`
}

export async function sha256Hex(s: string) {
  return toHex(await crypto.subtle.digest('SHA-256', enc.encode(s)))
}

async function hmacKey(secret: string | Uint8Array, hash: 'SHA-256' | 'SHA-1' = 'SHA-256') {
  const raw = typeof secret === 'string' ? enc.encode(secret) : secret
  return crypto.subtle.importKey('raw', raw as BufferSource, { name: 'HMAC', hash }, false, ['sign'])
}

export async function hmacHex(secret: string, data: string, hash: 'SHA-256' | 'SHA-1' = 'SHA-256') {
  return toHex(await crypto.subtle.sign('HMAC', await hmacKey(secret, hash), enc.encode(data)))
}

export async function hmacBase64(secret: string | Uint8Array, data: string, hash: 'SHA-256' | 'SHA-1' = 'SHA-256') {
  const sig = new Uint8Array(await crypto.subtle.sign('HMAC', await hmacKey(secret, hash), enc.encode(data)))
  return btoa(String.fromCharCode(...sig))
}

/** Constant-time string comparison. */
export function safeEqual(a: string, b: string) {
  if (a.length !== b.length) return false
  let r = 0
  for (let i = 0; i < a.length; i++) r |= a.charCodeAt(i) ^ b.charCodeAt(i)
  return r === 0
}

/** takeit4me's own webhook signature: `t=<unix>,v1=<hex hmac(secret, t.body)>`. */
export async function signPayload(secret: string, body: string, nowSeconds = Math.floor(Date.now() / 1000)) {
  return `t=${nowSeconds},v1=${await hmacHex(secret, `${nowSeconds}.${body}`)}`
}

export async function verifySignature(secret: string, body: string, header: string | null | undefined, toleranceSeconds = 300, nowSeconds = Math.floor(Date.now() / 1000)) {
  if (!header) return false
  const parts = Object.fromEntries(header.split(',').map((p) => p.trim().split('=') as [string, string]))
  const t = Number(parts.t)
  if (!Number.isFinite(t) || Math.abs(nowSeconds - t) > toleranceSeconds) return false
  return safeEqual(await hmacHex(secret, `${t}.${body}`), String(parts.v1 ?? ''))
}

/** Standard Webhooks verification (used by OpenAI webhooks): `webhook-id`, `webhook-timestamp`, `webhook-signature: v1,<base64>`. */
export async function verifyStandardWebhook(secret: string, headers: { id: string | null; timestamp: string | null; signature: string | null }, body: string, toleranceSeconds = 300) {
  if (!headers.id || !headers.timestamp || !headers.signature) return false
  const ts = Number(headers.timestamp)
  if (!Number.isFinite(ts) || Math.abs(Math.floor(Date.now() / 1000) - ts) > toleranceSeconds) return false
  const key = secret.startsWith('whsec_') ? Uint8Array.from(atob(secret.slice(6)), (c) => c.charCodeAt(0)) : enc.encode(secret)
  const expected = await hmacBase64(key, `${headers.id}.${headers.timestamp}.${body}`)
  return headers.signature.split(' ').some((s) => {
    const [, sig] = s.split(',')
    return sig ? safeEqual(sig, expected) : false
  })
}

// ---------------------------------------------------------------------------
// Secrets at rest (mailbox OAuth tokens): AES-256-GCM with a key held as a Worker secret.
// ---------------------------------------------------------------------------
const b64url = (b: Uint8Array) => btoa(String.fromCharCode(...b)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
const unb64url = (s: string) => Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4)), (c) => c.charCodeAt(0))

async function aesKey(keyB64: string) {
  let raw: Uint8Array
  try { raw = Uint8Array.from(atob(keyB64.trim()), (c) => c.charCodeAt(0)) } catch { throw new Error('The token encryption key is not valid base64') }
  if (raw.byteLength !== 32) throw new Error('The token encryption key must be 32 bytes (base64 of 32 random bytes)')
  return crypto.subtle.importKey('raw', raw as BufferSource, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt'])
}

/** A key's id: 8 hex characters of a hash of the key, written into each sealed value so it opens with the right key. */
export async function keyId(keyB64: string) {
  return (await sha256Hex(`kid:${keyB64.trim()}`)).slice(0, 8)
}

/**
 * Seals a secret as `v2.<key id>.<iv>.<ciphertext>` (base64url). `context` is bound in as additional data, so a
 * sealed value opens only for the same context (the company and connection it belongs to): a copied row is useless.
 */
export async function sealSecret(keyB64: string, plaintext: string, context = ''): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: enc.encode(context) }, await aesKey(keyB64), enc.encode(plaintext)))
  return `v2.${await keyId(keyB64)}.${b64url(iv)}.${b64url(ct)}`
}

/** The id of the key a sealed value names (null for values sealed before key ids). */
export function sealedKeyId(sealed: string): string | null {
  const p = sealed.split('.')
  return p[0] === 'v2' && p.length === 4 ? p[1]! : null
}

/**
 * Opens a value sealed by `sealSecret` with one key or a key ring (the current key first, then the previous one
 * during a rotation); throws if no key, the context or the value is wrong.
 */
export async function openSecret(keys: string | (string | null | undefined)[], sealed: string, context = ''): Promise<string> {
  const ring = (Array.isArray(keys) ? keys : [keys]).filter((k): k is string => !!k)
  const p = sealed.split('.')
  const open = async (k: string, iv: string, ct: string) => new TextDecoder().decode(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: unb64url(iv) as BufferSource, additionalData: enc.encode(context) }, await aesKey(k), unb64url(ct) as BufferSource))
  if (p[0] === 'v2' && p.length === 4) {
    for (const k of ring) if ((await keyId(k)) === p[1]) return open(k, p[2]!, p[3]!)
    throw new Error(`Sealed with a key this cell no longer holds (key ${p[1]})`)
  }
  if (p[0] === 'v1' && p.length === 3) {
    let last: unknown = null
    for (const k of ring) { try { return await open(k, p[1]!, p[2]!) } catch (e) { last = e } }
    throw last ?? new Error('No key to open the sealed secret')
  }
  throw new Error('Not a sealed secret')
}

/** PKCE (RFC 7636): a random verifier and its S256 challenge. */
export async function pkcePair() {
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(32)))
  const challenge = b64url(new Uint8Array(await crypto.subtle.digest('SHA-256', enc.encode(verifier))))
  return { verifier, challenge }
}
